<!-- One app dialog in place of the browser's alert / confirm / prompt (src/stores/notices.ts: uiConfirm, uiPrompt, uiAlert).
     Same markup and classes as the legacy uiDialog (.ui-dialog-wrap / .ui-dialog, animations in legacy.css). The keyboard
     is handled by the store (Enter = OK, Esc = cancel, other keys stopped); here: the buttons, a click outside (cancel,
     OK on an alert) and the cursor in the field (text selected) or on OK shortly after it opens. -->
<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { DIALOG_FOCUS_MS, outsideAnswer } from '../../features/notices'
import { useNoticesStore, type UiDialogItem } from '../../stores/notices'

const props = defineProps<{ item: UiDialogItem }>()
const notices = useNoticesStore()
const wrap = ref<HTMLElement | null>(null)
const inp = ref<HTMLInputElement | null>(null)
const okBtn = ref<HTMLButtonElement | null>(null)

onMounted(() => {
  if (wrap.value) notices.attach(props.item.id, wrap.value)
  setTimeout(() => { if (inp.value) { inp.value.focus(); inp.value.select() } else okBtn.value?.focus() }, DIALOG_FOCUS_MS)
})

function click(e: MouseEvent) {
  const b = (e.target as Element).closest('[data-a]') as HTMLElement | null
  if (b) notices.answer(props.item.id, b.dataset.a === '1')
  else if (e.target === wrap.value) notices.answer(props.item.id, outsideAnswer(props.item))
}
// The item is the store's reactive entry: the store reads the typed text when the dialog is answered
const typed = (e: Event) => { props.item.value = (e.target as HTMLInputElement).value }
</script>

<template>
  <div ref="wrap" class="ui-dialog-wrap fixed inset-0 z-[99] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" @click="click">
    <div class="ui-dialog bg-darkcard border border-gray-800 w-full max-w-sm rounded-3xl shadow-2xl p-5 md:p-6" role="dialog" aria-modal="true">
      <div class="flex items-start gap-3">
        <span :class="['w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0', item.danger ? 'bg-brand text-white' : 'bg-brand/15 text-brand']"><i :class="['fas', item.icon]"></i></span>
        <div class="min-w-0 flex-1 pt-0.5"><h3 v-if="item.title" class="font-black text-gray-100 mb-1">{{ item.title }}</h3><p class="text-sm text-gray-300 whitespace-pre-line break-words select-text">{{ item.message }}</p></div>
      </div>
      <input v-if="item.input !== null" ref="inp" class="ui-dialog-input mt-4 w-full bg-darkinput border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:border-brand select-text" :value="item.value" @input="typed">
      <div class="flex justify-end gap-2 mt-5">
        <button v-if="item.cancel" type="button" data-a="0" class="px-4 py-2.5 rounded-xl bg-darkbtn text-gray-300 hover:text-gray-100 text-sm font-bold">{{ item.cancel }}</button>
        <button ref="okBtn" type="button" data-a="1" :class="['px-5 py-2.5 rounded-xl bg-brand hover:bg-brandDark text-white text-sm font-bold', { 'ring-2 ring-brand/40 ring-offset-2 ring-offset-darkcard': item.danger }]"><i v-if="item.danger" class="fas fa-trash mr-1.5"></i>{{ item.ok }}</button>
      </div>
    </div>
  </div>
</template>
